import { useEffect, useState } from "react";
import { ShieldCheck, Download, HeartHandshake, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { PublicNav } from "@/components/PublicNav";
import { Button } from "@/components/ui/button";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Transparency() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/transparency").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const downloadReport = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/transparency/report.csv`, "_blank");
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <PublicNav />
      <section className="pt-28 pb-12 bg-emerald-900 text-emerald-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-emerald-300/80 font-medium">
            <ShieldCheck className="w-4 h-4" /> Full financial transparency
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-white mt-4">
            Where every dollar goes.
          </h1>
          <p className="text-emerald-100/85 mt-4 max-w-2xl leading-relaxed">
            {data?.fee_disclosure || "All proceeds are donated to benefit the Partnership to End Addiction."}
          </p>
          <a
            href={data?.charity?.url || "https://drugfree.org"}
            target="_blank"
            rel="noreferrer"
            data-testid="transparency-charity-link"
            className="inline-flex items-center gap-1.5 mt-5 text-white font-medium underline underline-offset-4 hover:text-emerald-200"
          >
            <HeartHandshake className="w-4 h-4" /> {data?.charity?.name || "Partnership to End Addiction"}
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-8 -mt-8">
        <div className="grid sm:grid-cols-3 gap-5" data-testid="transparency-totals">
          <StatCard label="Total raised" value={money(data?.total_raised)} tone="stone" testid="stat-raised" />
          <StatCard label="Total donated to charity" value={money(data?.total_remitted)} tone="emerald" testid="stat-remitted" />
          <StatCard label="Awaiting transfer" value={money(Math.max(0, data?.balance_pending || 0))} tone="orange" testid="stat-pending" />
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-2xl font-medium text-stone-900">Donations to the charity</h2>
          <Button data-testid="download-report-btn" onClick={downloadReport} variant="outline" className="rounded-full">
            <Download className="w-4 h-4 mr-2" /> Download report (CSV)
          </Button>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="remittance-table">
            <thead>
              <tr className="text-left text-xs tracking-widest uppercase text-stone-500 border-b border-stone-100">
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Method</th>
                <th className="px-6 py-4">Reference / Confirmation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {(data?.remittances || []).map((r) => (
                <tr key={r.id} data-testid={`remittance-row-${r.id}`}>
                  <td className="px-6 py-4 text-stone-700">{r.remitted_at}</td>
                  <td className="px-6 py-4 font-medium text-emerald-800">{money(r.amount)}</td>
                  <td className="px-6 py-4 capitalize text-stone-600">{r.method}</td>
                  <td className="px-6 py-4 text-stone-600">{r.reference || "—"}</td>
                </tr>
              ))}
              {(!data?.remittances || data.remittances.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-stone-500">
                    No transfers recorded yet. Every dollar raised is collected and donated to {data?.charity?.name || "the charity"} — this list updates as each gift is sent.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-stone-400 mt-4">
          Records reflect actual disbursements made by the Gather team to the charity partner. Reference numbers correspond to bank ACH, wire, or check confirmations.
        </p>
      </section>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const color = tone === "emerald" ? "text-emerald-800" : tone === "orange" ? "text-orange-700" : "text-stone-900";
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm" data-testid={testid}>
      <p className="text-xs tracking-widest uppercase text-stone-500">{label}</p>
      <p className={`font-heading text-3xl font-semibold mt-2 ${color}`}>{value}</p>
    </div>
  );
}
